import React, { useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { BiCopy, BiHide, BiShow, BiSolidPlug, BiX } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import { useGetScriptQuery } from "../../app/services/chatbot-rtk.services";
import { AlertsType } from "../../constants/alerts.constant";
import { Integrations } from "../../constants/Integrations.constant";
import { MultiLanguageUrl } from "../../constants/multiLanguage.constant";
import usePermission from "../../hooks/usePermission";
import { BotTypeEnum } from "../../utils/BotTypeEnum";
import { botsUiState, handleToggleBotsIntegrationModal } from "./botSlice";

const BotIntegrationModal = () => {
  const { t, i18n } = useTranslation();
  const navigation = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const { integrationModalData } = useAppSelector((state) => state.bots.ui);
  const { showIntegrationModal } = useAppSelector(botsUiState);
  const [showFullScript, setShowFullScript] = useState(false);

  // const [getScript, getScriptResults] = useLazyGetScriptQuery();
  const { data, isFetching, isError, isSuccess, refetch } = useGetScriptQuery(
    integrationModalData.botId,
    {
      skip: !integrationModalData.botId,
      refetchOnMountOrArgChange: true
    }
  );

  return (
    <Modal
      show={showIntegrationModal}
      centered
      onHide={() =>
        dispatch(handleToggleBotsIntegrationModal({ status: false }))
      }
      className="use-assistant-modal p-0"
      size="lg"
    >
      <div className="tw-relative tw-w-full tw-h-full tw-flex lg:tw-flex-row tw-flex-col">
        <div className="tw-absolute tw-top-0 tw-right-0 tw-m-4 tw-cursor-pointer">
          <BiX
            onClick={() => {
              dispatch(handleToggleBotsIntegrationModal({ status: false }));
            }}
            className="tw-text-black tw-w-5 tw-h-5"
          />
        </div>
        <div className="tw-px-12 tw-py-8   tw-bg-gray-50 tw-rounded-tl-2xl tw-rounded-bl-2xl tw-flex-col tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
          <div className="tw-w-40 tw-h-40 ">
            <BiSolidPlug className="tw-w-full tw-h-full tw-text-gray-700 " />
          </div>
        </div>
        <div className="lg:tw-pl-12 tw-pl-4 lg:tw-pr-3 tw-pr-4 tw-py-3  tw-bg-white tw-rounded-tr-2xl tw-rounded-br-2xl tw-justify-start tw-items-start tw-gap-8 tw-inline-flex">
          <div className="tw-grow tw-shrink tw-basis-0 tw-py-5 tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
            <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
              <div className="tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
                <div className="tw-text-gray-800 tw-text-3xl tw-font-semibold tw-font-['Lexend'] tw-leading-9">
                  {t("IntegrationsPage/modal-title")}
                </div>
                <div className="tw-w-5 tw-h-5 tw-relative tw-origin-top-left tw-rotate-180 tw- tw-overflow-hidden" />
              </div>
              <div className="tw-self-stretch tw-text-gray-500 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                {t("IntegrationsPage/modal-description")}
              </div>
            </div>
            <div className="tw-self-stretch tw-p-4  tw-bg-gray-100 tw-rounded-2xl tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
              <div className="tw-self-stretch tw-justify-between tw-items-start tw-flex  tw-gap-2  tw-flex-col">
                <div className="lg:tw-w-2/4 tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                  <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                    {integrationModalData?.title}
                  </div>
                  <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {integrationModalData?.botCode ===
                      BotTypeEnum.TEXT_SCENARIO ||
                    integrationModalData?.botCode === BotTypeEnum.VIDEO_SCENARIO
                      ? t("IntegrationsPage/scenario")
                      : t("IntegrationsPage/ai")}
                  </div>
                </div>
                <div className="lg:tw-justify-end tw-items-center tw-gap-3 tw-flex lg:tw-flex-row tw-flex-col-reverse tw-w-full lg:tw-mt-0 tw-mt-4 ">
                  <div
                    onClick={() => {
                      setShowFullScript((prev) => !prev);
                    }}
                    className="tw-w-full  tw-cursor-pointer tw-pl-2.5 tw-pr-3 tw-py-1.5 tw-bg-white tw-rounded-full tw-shadow-sm tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-flex tw-overflow-hidden"
                  >
                    {showFullScript ? (
                      <BiHide className="tw-w-4 tw-h-4 tw-relative tw- tw-overflow-hidden" />
                    ) : (
                      <BiShow className="tw-w-4 tw-h-4 tw-relative tw- tw-overflow-hidden" />
                    )}

                    <div className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {showFullScript
                        ? t("IntegrationsPage/hideScript")
                        : t("IntegrationsPage/showScript")}
                    </div>
                  </div>
                  <CopyToClipboard
                    text={isSuccess ? data?.script : ""}
                    onCopy={() =>
                      isSuccess && toast.success(t(AlertsType.ISSCKO001))
                    }
                  >
                    <button
                      disabled={isFetching && !isError}
                      onClick={isError ? refetch : undefined}
                      className={`${
                        isError
                          ? "tw-bg-gray-400"
                          : "tw-bg-primary disabled:tw-opacity-50"
                      } tw-w-full  tw-text-white tw-h-8 tw-text-sm tw-font-medium font-['Lexend'] tw-leading-tight tw-border-0 tw-rounded-full tw-pl-2.5 tw-pr-3 tw-py-1.5 `}
                    >
                      {isError ? (
                        "Retry"
                      ) : (
                        <React.Fragment>
                          {isFetching ? (
                            <Spinner size="sm" className="tw-mr-2" />
                          ) : (
                            <BiCopy size={22} className="tw-mr-2" />
                          )}
                          {t("WidgetContent/btn-02")}
                        </React.Fragment>
                      )}
                    </button>
                  </CopyToClipboard>
                </div>
              </div>
              {showFullScript && (
                <div
                  style={{ wordBreak: "break-word" }}
                  className="tw-max-w-md  tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-pl-4 tw-pr-3 tw-py-2 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex "
                >
                  {data?.script}
                </div>
              )}
            </div>
            <div className="tw-self-stretch tw-flex-col tw-justify-center tw-items-start tw-gap-4 tw-flex">
              <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                {t("IntegrationsPage/modal-subTitle")}
              </div>
              <div className="tw-justify-start tw-items-start tw-gap-6 tw-inline-flex lg:tw-flex-nowrap tw-flex-wrap">
                {Integrations.slice(0, 4).map((item) => (
                  <div
                    className="tw-w-20 tw-h-24 tw-relative tw-cursor-pointer"
                    key={item.name}
                    onClick={() => {
                      window.open(
                        `${import.meta.env.VITE_UI_ADDRESS}/${
                          MultiLanguageUrl[i18n.language] || "en-US"
                        }/chatbot-integration`,
                        "_blank"
                      );

                      dispatch(
                        handleToggleBotsIntegrationModal({ status: false })
                      );
                    }}
                    onKeyDown={() => {
                      window.open(
                        `${import.meta.env.VITE_UI_ADDRESS}/${
                          MultiLanguageUrl[i18n.language] || "en-US"
                        }/chatbot-integration`,
                        "_blank"
                      );

                      dispatch(
                        handleToggleBotsIntegrationModal({ status: false })
                      );
                    }}
                  >
                    <div className="tw-w-20 tw-h-20 tw-left-0 tw-top-0 tw-absolute">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="tw-w-20 tw-h-20 tw-left-0 tw-top-0 tw-absolute tw-bg-gray-100 tw-rounded-2xl"
                      />
                      <div className="tw-w-14 tw-h-14 tw-left-[12px] tw-top-[12px] tw-absolute tw- tw-overflow-hidden" />
                    </div>
                    <div className="tw-w-16 tw-left-[8px] tw-top-[88px] tw-absolute tw-text-center tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {item.name}
                    </div>
                  </div>
                ))}
                <div className="tw-w-20 tw-h-24 tw-relative tw-cursor-pointer">
                  <div
                    onClick={() => {
                      window.open(
                        `${import.meta.env.VITE_UI_ADDRESS}/${
                          MultiLanguageUrl[i18n.language] || "en-US"
                        }/chatbot-integration`,
                        "_blank"
                      );

                      dispatch(
                        handleToggleBotsIntegrationModal({ status: false })
                      );
                    }}
                    onKeyDown={() => {
                      window.open(
                        `${import.meta.env.VITE_UI_ADDRESS}/${
                          MultiLanguageUrl[i18n.language] || "en-US"
                        }/chatbot-integration`,
                        "_blank"
                      );

                      dispatch(
                        handleToggleBotsIntegrationModal({ status: false })
                      );
                    }}
                    role="button"
                    aria-label="copy script"
                    tabIndex={-1}
                    className="tw-cursor-pointer tw-h-20  tw-bg-gray-100 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-p-2"
                  >
                    <svg
                      width="36"
                      height="36"
                      viewBox="0 0 36 36"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 15C16.35 15 15 16.35 15 18C15 19.65 16.35 21 18 21C19.65 21 21 19.65 21 18C21 16.35 19.65 15 18 15ZM27 15C25.35 15 24 16.35 24 18C24 19.65 25.35 21 27 21C28.65 21 30 19.65 30 18C30 16.35 28.65 15 27 15ZM9 15C7.35 15 6 16.35 6 18C6 19.65 7.35 21 9 21C10.65 21 12 19.65 12 18C12 16.35 10.65 15 9 15Z"
                        fill="#889096"
                      />
                    </svg>
                  </div>
                  <div className=" tw-text-center tw-mt-2 tw-text-zinc-500 tw-text-xs tw-font-normal font-sofiaSans tw-leading-none">
                    {t("DashboardSettingCustomizeChat/other")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BotIntegrationModal;
